// ==========================================================================
// components/calendar/CalendarHero.tsx — the calendar's painted banner (FF-062)
// From the ChatGPT calendar pack (Calendar_Redesign_Draft_v1), merged 10 Oct
// 2026. James: "I like the nice cream and pink with the floral, it's soft but
// not basic."
//
// Presentation only: the Calendar screen owns the date, the view and every
// handler. The art is one of eight bundled SVGs (four Australian seasons ×
// desktop/phone) chosen from the month on screen; October is spring.
// All text and buttons are real HTML on top of the art.
// ==========================================================================
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import springDesktop from "@/assets/calendar/spring-desktop.svg";
import springMobile from "@/assets/calendar/spring-mobile.svg";
import summerDesktop from "@/assets/calendar/summer-desktop.svg";
import summerMobile from "@/assets/calendar/summer-mobile.svg";
import autumnDesktop from "@/assets/calendar/autumn-desktop.svg";
import autumnMobile from "@/assets/calendar/autumn-mobile.svg";
import winterDesktop from "@/assets/calendar/winter-desktop.svg";
import winterMobile from "@/assets/calendar/winter-mobile.svg";
import "./CalendarHero.css";

export type CalendarView = "week" | "month" | "agenda";
export type AustralianSeason = "spring" | "summer" | "autumn" | "winter";

const art: Record<AustralianSeason, { desktop: string; mobile: string }> = {
  spring: { desktop: springDesktop, mobile: springMobile },
  summer: { desktop: summerDesktop, mobile: summerMobile },
  autumn: { desktop: autumnDesktop, mobile: autumnMobile },
  winter: { desktop: winterDesktop, mobile: winterMobile },
};

/** Southern hemisphere: October is spring. */
export function australianSeason(date: Date): AustralianSeason {
  const m = date.getMonth();
  if (m === 11 || m <= 1) return "summer";
  if (m <= 4) return "autumn";
  if (m <= 7) return "winter";
  return "spring";
}

export default function CalendarHero({ displayDate, title, sub, view, onViewChange, onNavigate, onToday, onCreateEvent, tagline = "Good people, brighter days" }: {
  /** The date on screen: picks the season's art. */
  displayDate: Date;
  /** Big heading, e.g. "October" (or "Oct – Nov" for a week across two months). */
  title: string;
  /** Line under it, e.g. "2026" or "5 Oct – 11 Oct 2026". */
  sub: string;
  view: CalendarView;
  onViewChange: (v: CalendarView) => void;
  onNavigate: (direction: -1 | 1) => void;
  onToday: () => void;
  onCreateEvent: () => void;
  tagline?: string;
}) {
  const season = australianSeason(displayDate);
  const pair = art[season];
  const unit = view === "month" ? "month" : "week";
  return (
    <section className="oc-calendar-hero" aria-label={`${title} ${sub}`} data-season={season}>
      <picture className="oc-calendar-hero__art" aria-hidden="true">
        <source media="(max-width: 639px)" srcSet={pair.mobile} />
        <img src={pair.desktop} alt="" draggable={false} />
      </picture>
      <div className="oc-calendar-hero__veil" aria-hidden="true" />

      <div className="oc-calendar-hero__inside">
        <div className="oc-calendar-hero__top">
          <div className="oc-calendar-hero__date">
            <h1 className="oc-calendar-hero__month">{title}</h1>
            <span className="oc-calendar-hero__year">{sub}</span>
          </div>
          <nav className="oc-calendar-hero__nav" aria-label="Move the calendar">
            <button type="button" onClick={() => onNavigate(-1)} aria-label={`Previous ${unit}`} className="oc-calendar-hero__nav-button"><ChevronLeft aria-hidden size={19} strokeWidth={1.9} /></button>
            <button type="button" onClick={onToday} className="oc-calendar-hero__today">Today</button>
            <button type="button" onClick={() => onNavigate(1)} aria-label={`Next ${unit}`} className="oc-calendar-hero__nav-button"><ChevronRight aria-hidden size={19} strokeWidth={1.9} /></button>
          </nav>
        </div>

        <div className="oc-calendar-hero__bottom">
          {tagline && <p className="oc-calendar-hero__tagline" aria-hidden="true">{tagline}</p>}
          <div className="oc-calendar-hero__actions">
            <div className="oc-calendar-hero__views" role="group" aria-label="Calendar view">
              {(["week", "month", "agenda"] as const).map((v) => (
                <button key={v} type="button" aria-pressed={view === v} className="oc-calendar-hero__view" onClick={() => onViewChange(v)}>
                  {v[0].toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
            <button type="button" onClick={onCreateEvent} className="oc-calendar-hero__create"><Plus aria-hidden size={17} strokeWidth={2.1} /><span>Event</span></button>
          </div>
        </div>
      </div>
    </section>
  );
}
